import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import api, { getImageUrl } from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, MapPin, Search, Users, X } from 'lucide-react';

// Pick a character or location from this project (default) or any project in the workspace.
// Cross-project picks are copied into this project first (independent copy linked to the origin).
export function EntityPicker({ kind, projectId, excludeIds = [], onPick, onClose, title, initialTab = 'project', rawPick = false, note }) {
  const isChar = kind === 'character';
  const [tab, setTab] = useState(initialTab);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const load = async () => {
      try {
        if (tab === 'project') {
          const res = await api.get(`/projects/${projectId}/${isChar ? 'characters' : 'locations'}`);
          if (alive) setItems((Array.isArray(res.data) ? res.data : []).map(x => ({ ...x, project_name: '' })));
        } else {
          const res = await api.get(`/workspace/${isChar ? 'characters' : 'locations'}`);
          const list = res.data[isChar ? 'characters' : 'locations'] || [];
          if (alive) setItems(list.filter(x => x.project_id !== projectId));
        }
      } catch { if (alive) { setItems([]); toast.error(`Could not load ${isChar ? 'characters' : 'locations'}`); } }
      finally { if (alive) setLoading(false); }
    };
    load();
    return () => { alive = false; };
  }, [tab, projectId, isChar]);

  const visible = useMemo(() => {
    const s = q.trim().toLowerCase();
    return items.filter(x => !excludeIds.includes(x.id) && (!s || (x.name || '').toLowerCase().includes(s) || (x.project_name || '').toLowerCase().includes(s)));
  }, [items, q, excludeIds]);

  const pick = async (item) => {
    setBusyId(item.id);
    try {
      if (tab === 'project' || rawPick) { await onPick(item); }
      else {
        const res = await api.post(`/projects/${projectId}/${isChar ? 'characters' : 'locations'}/import`, { source_id: item.id });
        await onPick(res.data, true);
      }
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not add'); }
    finally { setBusyId(null); }
  };

  const img = (x) => isChar ? x.image_storage_path : x.image_path;
  const Icon = isChar ? Users : MapPin;
  return createPortal(
    <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-[2px]" data-testid={`entity-picker-${kind}`} onClick={onClose}>
      <div data-testid="slide-over-panel" className="absolute right-0 top-0 h-full w-full max-w-[600px] bg-[#0E0E12] border-l border-white/10 shadow-2xl flex flex-col animate-slide-in-right p-5" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-semibold text-white flex items-center gap-2"><Icon className="w-4 h-4 text-[#FF4500]" /> {title || (isChar ? 'Add character to scene' : 'Choose a location')}</p>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/10 text-[#8A8A93]" data-testid="entity-picker-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <div className="flex gap-1 p-0.5 rounded-lg bg-white/[0.04] border border-white/[0.06]">
            {[{ id: 'project', label: isChar ? 'Characters in this project' : 'Locations in this project' }, { id: 'workspace', label: isChar ? 'Characters in all projects' : 'Locations in all projects' }].map(t => (
              <button key={t.id} data-testid={`entity-tab-${t.id}`} onClick={() => setTab(t.id)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${tab === t.id ? 'bg-[#FF4500] text-white' : 'text-[#8A8A93] hover:text-white'}`}>{t.label}</button>
            ))}
          </div>
          <div className="relative ml-auto">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#55555C]" />
            <input data-testid="entity-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Search…" className="ff-input pl-8 text-xs py-1.5 w-48" />
          </div>
        </div>
        {note ? <p className="text-[10px] text-[#8A8A93] mb-2">{note}</p> : tab === 'workspace' && <p className="text-[10px] text-[#8A8A93] mb-2">Picking from another project copies it into this one (edits stay independent).</p>}
        <div className="overflow-y-auto flex-1 min-h-[160px]">
          {loading ? <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-[#8A8A93]" /></div>
            : visible.length === 0 ? <p className="text-xs text-[#55555C] text-center py-10">{tab === 'project' ? `No more ${isChar ? 'characters' : 'locations'} in this project.` : `No ${isChar ? 'characters' : 'locations'} in other projects yet.`}</p>
            : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {visible.map(x => (
                  <button key={x.id} data-testid={`entity-item-${x.id}`} onClick={() => pick(x)} disabled={busyId === x.id}
                    className="text-left rounded-lg overflow-hidden border border-white/[0.06] bg-[#141418] hover:border-[#FF4500]/50 transition-all disabled:opacity-60">
                    <div className={`${isChar ? 'aspect-square' : 'aspect-video'} bg-[#0a0a0e] relative`}>
                      {img(x) ? <img src={getImageUrl(img(x))} alt="" className="w-full h-full object-cover" /> : <Icon className="w-6 h-6 text-[#55555C] absolute inset-0 m-auto" />}
                      {busyId === x.id && <div className="absolute inset-0 bg-black/60 flex items-center justify-center"><Loader2 className="w-4 h-4 animate-spin text-white" /></div>}
                    </div>
                    <div className="p-2">
                      <p className="text-xs text-white font-medium truncate">{x.name}</p>
                      <p className="text-[10px] text-[#55555C] truncate">{x.project_name || (isChar ? (x.character_type || 'main') : (x.description || '—'))}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
        </div>
      </div>
    </div>,
    document.body
  );
}
